import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { LeafletMap } from "@/components/LeafletMap";
import { fmtDate, fmtTime } from "@/lib/format";
import { ArrowLeft, Download, Loader2, CheckCircle2, AlertTriangle, Truck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PODView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["pod", id], queryFn: async () => (await api.get(`/orders/${id}/pod`)).data });

  if (isLoading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#FF6500]" /></div>;
  if (!data) return <div className="py-20 text-center text-slate-400">POD not available</div>;

  const o = data.order; const c = data.company || {}; const d = o.delivery || {}; const cs = o.customer_snapshot || {};
  const Row = ({ label, value }) => (
    <div><div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div><div className="font-semibold text-[#0B192C]">{value || "-"}</div></div>
  );

  return (
    <div className="mx-auto max-w-3xl animate-slide-up">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500"><ArrowLeft size={18} /> Back</button>
        <button onClick={() => window.open(`${API}/api/orders/${id}/pod/pdf`, "_blank")} data-testid="pod-download-pdf-btn" className="flex items-center gap-2 rounded-xl bg-[#0B192C] px-4 py-2.5 text-sm font-bold text-white tap-shrink">
          <Download size={16} /> Download PDF
        </button>
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="pod-document">
        {/* header */}
        <div className="flex items-center justify-between bg-[#0B192C] px-6 py-5 text-white">
          <div>
            <h1 className="font-head text-2xl font-black">PROOF OF DELIVERY</h1>
            <p className="text-xs text-slate-300">{c.company_name || "GS Pacific Distribution"} · ABN {c.abn || "48 102 938 481"}</p>
          </div>
          <div className="text-right">
            <div className="flex items-center justify-end gap-2 font-head text-lg font-extrabold text-[#FF6500]"><Truck size={20} /> GS PACIFIC</div>
            <p className="mt-1 font-mono text-xs text-slate-300">{o.pod_number}</p>
          </div>
        </div>

        <div className="space-y-6 p-6">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Row label="Order" value={`#${o.order_number}`} />
            <Row label="Customer" value={cs.name} />
            <Row label="Driver" value={o.driver_name} />
            <Row label="Delivery Date" value={fmtDate(d.completed_at || o.delivery_date)} />
          </div>
          <Row label="Delivery Address" value={cs.delivery_address} />

          <div className="grid grid-cols-3 gap-4">
            <Row label="Arrival" value={fmtTime(d.arrived_at)} />
            <Row label="Completion" value={fmtTime(d.completed_at)} />
            <Row label="Time on Site" value={d.time_on_site_minutes != null ? `${d.time_on_site_minutes} min` : "-"} />
          </div>

          <div className="rounded-xl bg-slate-50 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Goods Delivered</div>
            <div className="mt-2 grid grid-cols-4 gap-4">
              <div><div className="font-head text-2xl font-black text-[#0B192C]">{d.pallets_delivered ?? o.pallets}</div><div className="text-xs text-slate-400">Pallets</div></div>
              <div><div className="font-head text-2xl font-black text-[#0B192C]">{d.cartons_delivered ?? o.cartons}</div><div className="text-xs text-slate-400">Cartons</div></div>
              <div><div className="font-head text-2xl font-black text-[#0B192C]">{d.items_delivered ?? o.total_items}</div><div className="text-xs text-slate-400">Items</div></div>
              <div><div className="font-semibold text-[#0B192C]">{d.condition || "Delivered in full"}</div><div className="text-xs text-slate-400">Condition</div></div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Received By</div>
              <div className="font-head text-lg font-bold text-[#0B192C]">{d.receiver_name || "-"}</div>
              {d.receiver_company && <div className="text-sm text-slate-500">{d.receiver_company}</div>}
              <div className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><CheckCircle2 size={15} /> SIGNED {fmtTime(d.signed_at)}</div>
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Signature</div>
              {d.signature ? <img src={d.signature} alt="signature" className="mt-1 h-24 rounded-lg border border-slate-200 bg-white p-1" /> : <div className="text-slate-400">-</div>}
            </div>
          </div>

          {/* GPS */}
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Delivery Location (GPS)</div>
            <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2 text-sm">
                <div className="font-mono text-[#0B192C]">{d.arrival_lat != null ? `${d.arrival_lat.toFixed(6)}, ${d.arrival_lng.toFixed(6)}` : "-"}</div>
                <div className="text-slate-500">Accuracy: {d.gps_accuracy ?? "-"} m</div>
                {d.location_confirmed ? (
                  <div className="flex items-center gap-1.5 font-semibold text-emerald-700"><CheckCircle2 size={15} /> Location Confirmed</div>
                ) : (
                  <div className="flex items-center gap-1.5 font-semibold text-amber-700"><AlertTriangle size={15} /> {d.distance_from_customer}m from registered address</div>
                )}
              </div>
              {d.arrival_lat != null && <LeafletMap height={150} points={[{ lat: d.arrival_lat, lng: d.arrival_lng, status: o.status, label: "Delivery location" }]} />}
            </div>
          </div>

          {(d.photos || []).length > 0 && (
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Delivery Photos</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {d.photos.map((p) => <img key={p.id} src={p.data} alt={p.category} className="h-28 w-28 rounded-lg object-cover" />)}
              </div>
            </div>
          )}

          {(d.condition_notes || o.delivery_notes) && <Row label="Driver Notes" value={d.condition_notes || o.delivery_notes} />}
        </div>
      </div>
    </div>
  );
}
